/**
 * Tabs Component
 * Handles tab navigation and content switching
 */
class TabsComponent extends HTMLElement {
  /** @type {NodeListOf<HTMLButtonElement>} */
  get tabs() {
    return this.querySelectorAll('[role="tab"]');
  }

  /** @type {NodeListOf<HTMLElement>} */
  get panels() {
    return this.querySelectorAll('[role="tabpanel"]');
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    // Add click listeners to all tabs
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.#handleTabClick, { signal });
    });

    // Add keyboard navigation
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Handles tab click events
   * @param {Event} event - The click event
   */
  #handleTabClick = (event) => {
    const clickedTab = event.currentTarget;
    if (!(clickedTab instanceof HTMLButtonElement)) return;

    const tabId = clickedTab.dataset.tabId;
    if (!tabId) return;

    this.#switchToTab(tabId);
  };

  /**
   * Handles keyboard navigation
   * @param {KeyboardEvent} event - The keyboard event
   */
  #handleKeyDown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLButtonElement) || target.getAttribute('role') !== 'tab') {
      return;
    }

    const tabs = Array.from(this.tabs);
    const currentIndex = tabs.indexOf(target);

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        nextIndex = currentIndex - 1;
        if (nextIndex < 0) nextIndex = tabs.length - 1;
        break;
      case 'ArrowRight':
        event.preventDefault();
        nextIndex = currentIndex + 1;
        if (nextIndex >= tabs.length) nextIndex = 0;
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    const nextTab = tabs[nextIndex];
    if (nextTab) {
      const tabId = nextTab.dataset.tabId;
      if (tabId) {
        this.#switchToTab(tabId);
        nextTab.focus();
      }
    }
  };

  /**
   * Switches to the specified tab
   * @param {string} tabId - The ID of the tab to switch to
   */
  #switchToTab(tabId) {
    // Update tab buttons
    this.tabs.forEach((tab) => {
      const isSelected = tab.dataset.tabId === tabId;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    // Update panels
    this.panels.forEach((panel) => {
      const panelId = `tab-panel-${tabId}`;
      const isVisible = panel.id === panelId;
      
      if (isVisible) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}

